import { useEffect, useState } from "react";
import { ChevronRight, CheckCircle2, AlertTriangle, XCircle, Ship } from "lucide-react";
import { Button, ChartCard, Skeleton } from "@/components/reusable";
import { fetchReviewQueue } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { ReviewQueueRow, Severity } from "@/types/analytics";

function usdCompact(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

const ROW_FLAG_CLASS: Record<Severity, string> = {
  danger: "bg-[var(--color-error-light)] text-[#791F1F]",
  warn: "bg-[var(--color-warning-light)] text-[#854F0B]",
  info: "bg-[#E6F1FB] text-[#0C447C]",
  neutral: "bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]",
  ok: "bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)]",
};

function DqBadge({ row }: { row: ReviewQueueRow }) {
  if (row.dqStatus === "pass") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--color-brand-primary)]/10 text-[var(--color-brand-primary-dark)] uppercase tracking-wide">
        <CheckCircle2 className="w-3 h-3" />
        Passed
      </span>
    );
  }
  if (row.dqFailureCount === 1) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--color-warning-light)] text-[#854F0B] uppercase tracking-wide">
        <AlertTriangle className="w-3 h-3" />1 failure
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[var(--color-error-light)] text-[#791F1F] uppercase tracking-wide">
      <XCircle className="w-3 h-3" />
      {row.dqFailureCount} failures
    </span>
  );
}

interface VesselReviewQueueProps {
  /** Cap the table height. Full queue can be long — show "View all" after the cap. */
  initialLimit?: number;
}

export function VesselReviewQueue({ initialLimit = 8 }: VesselReviewQueueProps) {
  const filters = useAnalyticsFilters();
  const [rows, setRows] = useState<ReviewQueueRow[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    setRows(null);
    fetchReviewQueue(filters).then((r) => {
      if (active) setRows(r);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  const visible = rows ? (expanded ? rows : rows.slice(0, initialLimit)) : [];
  const hiddenCount = rows ? Math.max(0, rows.length - visible.length) : 0;

  return (
    <ChartCard
      title="Vessel review queue"
      subtitle={
        rows
          ? `${visible.length}${hiddenCount ? ` of ${rows.length}` : ""} vessel${visible.length === 1 ? "" : "s"} · DQ failures and underwriting attention items`
          : "Loading…"
      }
      headerRight={
        rows && hiddenCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "Collapse" : "View all"}
            <ChevronRight
              className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </Button>
        ) : null
      }
    >
      {!rows ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height="h-7" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyQueue />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] border-collapse min-w-[780px]">
            <thead>
              <tr>
                {[
                  "Vessel name",
                  "IMO",
                  "Type",
                  "Flag",
                  "Yr built",
                  "Sum insured",
                  "Class",
                  "DQ status",
                  "Flags",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-[10px] uppercase tracking-wider font-semibold text-[var(--color-text-tertiary)] pb-2.5 pr-3 border-b border-[var(--color-border)] text-left whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.imo} className="hover:bg-[var(--color-bg-secondary)] transition-colors">
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)]">
                    <div className="flex items-center gap-2">
                      <Ship className="w-3 h-3 text-[#378ADD] flex-shrink-0" />
                      <span className="font-medium text-[var(--color-text-primary)]">{r.name}</span>
                    </div>
                  </td>
                  <td
                    className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[11px] text-[var(--color-text-secondary)]"
                    style={{ fontFamily: "var(--font-mono)" }}
                  >
                    {r.imo}
                  </td>
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[var(--color-text-primary)]">
                    {r.type}
                  </td>
                  <td
                    className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[11px] text-[var(--color-text-secondary)]"
                    style={{ fontFamily: "var(--font-mono)" }}
                  >
                    {r.flagIso2}
                  </td>
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[var(--color-text-primary)]">
                    {r.yearBuilt}
                  </td>
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[var(--color-text-primary)]">
                    {usdCompact(r.siUsd)}
                  </td>
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)] text-[var(--color-text-primary)]">
                    {r.classSociety}
                  </td>
                  <td className="py-2 pr-3 border-b border-[var(--color-border-light)]">
                    <DqBadge row={r} />
                  </td>
                  <td className="py-2 pr-0 border-b border-[var(--color-border-light)]">
                    <div className="flex gap-1 flex-wrap">
                      {r.rowFlags.map((f, i) => (
                        <span
                          key={`${f.label}-${i}`}
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${ROW_FLAG_CLASS[f.tone]}`}
                        >
                          {f.label}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
}

function EmptyQueue() {
  return (
    <div className="py-10 text-center text-[12px] text-[var(--color-text-tertiary)]">
      No vessels match the current filters.
    </div>
  );
}
